import type { Component } from 'solid-js'
import { Show } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Table from '~/components/Table'
import type { CustomerAddressType } from '~/services/customer.service'
import type { ColumnType } from '~/types/Table'
import { P } from '~/utils/permissions'
import type { TabDireccionesProps } from './types'

const TabDirecciones: Component<TabDireccionesProps> = props => {
  const addressGeometryColumn: ColumnType<CustomerAddressType> = {
    header: 'Ubicación',
    accessor: (a: CustomerAddressType) => {
      if (a.polygon?.length) {
        return (
          <Button size="xs" variant="text" color="warning" class="p-1!" onClick={() => props.openAddressModal(a)} tooltip="Ver en el mapa" prependIcon="material-symbols-light:polyline-outline">
            Polígono · {a.polygon.length} pts
          </Button>
        )
      }
      if (typeof a.lat === 'number' && typeof a.lng === 'number') {
        return (
          <Button size="xs" variant="text" color="default" class="p-1!" onClick={() => props.openAddressModal(a)} tooltip="Ver en el mapa" prependIcon="material-symbols-light:check-circle">
            <span class="tabular-nums">
              {a.lat.toFixed(5)}, {a.lng.toFixed(5)}
            </span>
          </Button>
        )
      }
      return <span class="text-foreground-subtle">Sin ubicación</span>
    }
  }

  return (
    <Table<CustomerAddressType>
      title="Direcciones"
      headerActions={
        <Show when={props.can(P.customers.update)}>
          <Button size="sm" variant="soft" color="primary" onClick={() => props.openAddressModal()} prependIcon="material-symbols-light:add">
            Nueva dirección
          </Button>
        </Show>
      }
      data={props.data.addresses ?? []}
      keyField="id"
      columns={[
        {
          header: 'Etiqueta',
          accessor: (a: CustomerAddressType) => (
            <div class="flex items-center gap-1.5 flex-wrap">
              <Badge color={a.label === 'fiscal' ? 'info' : 'default'}>{a.label || 'Dirección'}</Badge>
              <Show when={a.principal}>
                <Badge color="success">Principal</Badge>
              </Show>
            </div>
          )
        },
        {
          header: 'Dirección',
          accessor: (a: CustomerAddressType) => (
            <div class="flex flex-col">
              <span class="font-medium text-foreground">{a.address}</span>
              <span class="text-xs text-foreground-subtle">{[a.barrio, a.codigoPostal ? `CP ${a.codigoPostal}` : null].filter(Boolean).join(' · ') || ''}</span>
            </div>
          )
        },
        { header: 'Ciudad', accessor: (a: CustomerAddressType) => [a.ciudad, a.departamento].filter(Boolean).join(', ') || '—' },
        addressGeometryColumn,
        {
          header: 'Creada',
          accessor: (a: CustomerAddressType) => new Date(a.createdAt).toLocaleDateString('es-CO')
        }
      ]}
      emptyMessage="Sin direcciones registradas"
      pagination={false}
      pinnable={false}
      actions={address => (
        <ButtonGroup size="sm" variant="soft">
          <Show when={props.can(P.customers.update)}>
            <Button color="warning" onClick={() => props.openAddressModal(address)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
          </Show>
          <Show when={props.can(P.customers.delete)}>
            <Button color="danger" onClick={() => props.removeAddress(address.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </Show>
        </ButtonGroup>
      )}
    />
  )
}

export default TabDirecciones
